import { Gauge } from 'lucide-react';
import React from 'react';

/**
 * Reusable "Coming Soon" placeholder component
 * 
 * Used for features that are planned but not yet implemented.
 * Provides consistent styling and messaging across the application.
 * 
 * @param {Object} props
 * @param {string} props.title - Title of the feature
 * @param {string} props.description - Description of what the feature will do
 * @param {React.ComponentType} [props.icon] - Optional custom icon component (defaults to Gauge)
 * @param {string} [props.badgeText] - Custom badge text (defaults to "Coming Soon")
 * @param {React.ReactNode} [props.children] - Optional background content to blur (preview widgets)
 */
export function ComingSoon({ 
  title, 
  description, 
  icon: Icon = Gauge,
  badgeText = "Coming Soon",
  children
}) {
  return (
    <div className="min-h-screen bg-lap-bg relative">
      {/* Background content with blur */}
      {children && (
        <div className="absolute inset-0 overflow-auto blur-sm pointer-events-none opacity-50">
          {children}
        </div>
      )}

      {/* Coming Soon overlay — floating glass sheet */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass rounded-card p-8">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-lap-accent/10 rounded-full">
              <Icon className="w-8 h-8 text-lap-accent" />
            </div>
            <h2 className="text-2xl font-extrabold mb-2">{title}</h2>
            <p className="text-lap-muted mb-6">{description}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-lap-accent/10 border border-lap-accent/20 rounded-full">
              <span className="eyebrow">{badgeText}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

